import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { stripeAPI } from '../services/api';
import { EMPLOYEE_COUNT_OPTIONS, BUSINESS_TYPE_OPTIONS } from '../utils/profileOptions';
import LoadingSpinner from '../components/LoadingSpinner';
import { UserIcon, CreditCardIcon, XMarkIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../utils/contractFormatting';
import { proBenefits } from '../utils/proBenefits';
import { authAPI } from '../services/api';
import { buildBackendUrl } from '../services/api';
import type { PlanDetails } from '../types';
import NotificationSettings from '../components/NotificationSettings';

const DEFAULT_PLAN_PRICE = 10;
const DEFAULT_PLAN_INTERVAL = 'month';
const GOOGLE_ACCOUNT_LINK = buildBackendUrl('/auth/google/link?next=%2Faccount');

const formatIntervalLabel = (details?: PlanDetails | null) => {
  const interval = (details?.billing_interval ?? DEFAULT_PLAN_INTERVAL).toLowerCase();
  const count = details?.billing_interval_count ?? 1;
  const suffix = interval.endsWith('s') ? '' : 's';
  if (count <= 1) {
    return interval;
  }
  return `${count} ${interval}${suffix}`;
};

const formatBillingSubtitle = (details?: PlanDetails | null) => {
  const interval = (details?.billing_interval ?? DEFAULT_PLAN_INTERVAL).toLowerCase();
  const count = details?.billing_interval_count ?? 1;
  if (count <= 1) {
    return interval === 'month' ? 'Billed monthly' : `Billed every ${interval}`;
  }
  const pluralLabel = formatIntervalLabel(details);
  return `Billed every ${pluralLabel}`;
};

const formatPlanPrice = (details?: PlanDetails | null) => {
  const amount = details?.price ?? DEFAULT_PLAN_PRICE;
  const intervalLabel = formatIntervalLabel(details);
  return `$${amount.toFixed(2)}/${intervalLabel}`;
};

const formatStatusLabel = (status?: string | null, isActive?: boolean) => {
  if (isActive) {
    return 'Active';
  }

  if (status === 'trialing') {
    return 'Expired';
  }

  if (!status) {
    return 'Inactive';
  }

  return status
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

type ProfileFormState = {
  full_name: string;
  email: string;
  business_name: string;
  business_type: string;
  employee_count: string;
  phone: string;
  city: string;
};

const emptyProfileState: ProfileFormState = {
  full_name: '',
  email: '',
  business_name: '',
  business_type: '',
  employee_count: '',
  phone: '',
  city: '',
};

const Account: React.FC = () => {
  const { user, updateProfile, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasSyncedSubscription = useRef(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [subscriptionBanner, setSubscriptionBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileFormState>(emptyProfileState);
  const [originalProfile, setOriginalProfile] = useState<ProfileFormState>(emptyProfileState);
  const [profileStatus, setProfileStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwStatus, setPwStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [pwResetSending, setPwResetSending] = useState(false);
  const [delConfirm, setDelConfirm] = useState('');
  const [delPassword, setDelPassword] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delStatus, setDelStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isOpeningBilling, setIsOpeningBilling] = useState(false);

useEffect(() => {
  if (subscriptionBanner) {
    const timeout = window.setTimeout(() => setSubscriptionBanner(null), 6000);
    return () => window.clearTimeout(timeout);
  }
  return undefined;
}, [subscriptionBanner]);

useEffect(() => {
  if (profileStatus) {
    const handle = window.setTimeout(() => setProfileStatus(null), 4000);
    return () => window.clearTimeout(handle);
  }
  return undefined;
}, [profileStatus]);

useEffect(() => {
  const stripeSubId = user?.subscription?.stripe_subscription_id;
  if (!hasSyncedSubscription.current && stripeSubId && stripeSubId.startsWith('sub_')) {
    hasSyncedSubscription.current = true;
    (async () => {
      try {
        await stripeAPI.syncSubscription();
      } catch (error) {
        console.error('Initial subscription sync failed', error);
      } finally {
        await refreshUser();
      }
    })();
  }
}, [user?.subscription?.stripe_subscription_id, refreshUser]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get('checkout');
    const upgraded = params.get('upgraded');
    const billingStatus = params.get('billing');

    if (upgraded === 'true') {
      setProfileStatus({ type: 'success', message: 'Welcome to Pro! Your subscription is now active.' });
      navigate(location.pathname, { replace: true });
    } else if (checkoutStatus === 'success') {
      setProfileStatus({ type: 'success', message: 'Your Pro subscription is now active.' });
      navigate(location.pathname, { replace: true });
    } else if (checkoutStatus === 'error') {
      setProfileStatus({ type: 'error', message: 'We could not activate your subscription. Please contact support.' });
      navigate(location.pathname, { replace: true });
    } else if (billingStatus === 'updated') {
      (async () => {
        try {
          await stripeAPI.syncSubscription();
        } catch (error) {
          console.error('Failed to sync subscription after portal update', error);
        }
        await refreshUser();
        setSubscriptionBanner({ type: 'success', message: 'Billing details updated.' });
        navigate(location.pathname, { replace: true });
      })();
    }
  }, [location.pathname, location.search, navigate, refreshUser]);

  useEffect(() => {
    if (user) {
      const businessType = user.business_type && BUSINESS_TYPE_OPTIONS.includes(user.business_type)
        ? user.business_type
        : user.business_type
          ? 'Other'
          : '';

      const employeeCount = user.employee_count && EMPLOYEE_COUNT_OPTIONS.includes(user.employee_count)
        ? user.employee_count
        : user.employee_count ?? '';

      const next: ProfileFormState = {
        full_name: user.full_name ?? '',
        email: user.email ?? '',
        business_name: user.business_name ?? '',
        business_type: businessType,
        employee_count: employeeCount,
        phone: user.phone ?? '',
        city: user.city ?? '',
      };
      setProfileForm(next);
      setOriginalProfile(next);
    }
  }, [user]);

  if (!user) {
    return <LoadingSpinner message="Loading account..." />;
  }

  const subscription = user.subscription;
  const planDetails = user.plan_details;
  const isPremium = Boolean(user.is_premium);
  const hasActiveSubscription = Boolean(subscription?.is_active);
  const trialEndRaw = subscription?.trial_end ?? null;
  const trialEndDate = trialEndRaw ? new Date(trialEndRaw) : null;
  const isTrialActive = Boolean(trialEndDate && trialEndDate.getTime() > Date.now());
  const trialEndsLabel = isTrialActive && trialEndRaw ? formatDate(trialEndRaw) : null;
  const hasFutureEndDate =
    subscription?.end_date && new Date(subscription.end_date).getTime() > Date.now();
  const cancelsOn =
    subscription?.status === 'canceled' && hasFutureEndDate ? formatDate(subscription.end_date) : null;
  const isCanceledAtPeriodEnd = Boolean(cancelsOn);
  const canManageBilling =
    Boolean(subscription?.stripe_subscription_id?.startsWith('sub_')) &&
    (hasActiveSubscription || isCanceledAtPeriodEnd);

  const nextBilling = subscription?.end_date ? formatDate(subscription.end_date) : 'Not scheduled';
  const billingLabel = isTrialActive ? 'Trial Ends' : 'Next Billing Date';
  const billingValue = isTrialActive ? (trialEndsLabel ?? '—') : (isPremium ? nextBilling : '—');
  const statusLabel = formatStatusLabel(subscription?.status, subscription?.is_active);
  const planLabel = planDetails?.nickname ?? 'Pro';
  const priceDisplay = formatPlanPrice(planDetails);
  const billingSubtitle = formatBillingSubtitle(planDetails);
  const profileChanged = JSON.stringify(profileForm) !== JSON.stringify(originalProfile);

  const handleProfileInputChange = (field: keyof ProfileFormState, value: string) => {
    setProfileForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancelEdit = () => {
    setProfileForm(originalProfile);
    setIsEditingProfile(false);
    setProfileStatus(null);
  };

  const handleProfileSubmit = async () => {
    if (!profileChanged) {
      setIsEditingProfile(false);
      return;
    }

    setIsSavingProfile(true);
    setProfileStatus(null);

    try {
      await updateProfile(profileForm);
      setProfileStatus({ type: 'success', message: 'Profile updated successfully.' });
      setOriginalProfile(profileForm);
      setIsEditingProfile(false);
    } catch (error: any) {
      const message = error?.response?.data?.error ?? 'Unable to update profile. Please try again.';
      setProfileStatus({ type: 'error', message });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const response = await stripeAPI.createCheckoutSession();
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Upgrade error:', error);
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Failed to start checkout. Please try again.';
      setSubscriptionBanner({ type: 'error', message });
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!hasActiveSubscription || isCanceling) {
      return;
    }

    setIsCanceling(true);
    setSubscriptionBanner(null);

    try {
      const response = await stripeAPI.cancelSubscription();
      const cancelsOnIso = response.data.cancels_on;
      const cancelsOn = cancelsOnIso ? formatDate(cancelsOnIso) : null;
      setSubscriptionBanner({
        type: 'success',
        message: cancelsOn
          ? `Your subscription will remain active through ${cancelsOn}.`
          : 'Your subscription will remain active through the current billing period.',
      });
      await refreshUser();
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      const message = error?.response?.data?.error ?? 'Failed to cancel the subscription. Please try again.';
      setSubscriptionBanner({ type: 'error', message });
    } finally {
      setIsCanceling(false);
    }
  };

  const handleManageBilling = async () => {
    if (!canManageBilling || isOpeningBilling || !subscription?.stripe_subscription_id) return;
    setIsOpeningBilling(true);
    try {
      const response = await stripeAPI.createBillingPortalSession();
      const portalUrl = response.data.url;
      if (portalUrl) {
        const portalWindow = window.open(portalUrl, '_blank', 'noopener');
        if (!portalWindow) {
          const proceed = window.confirm(
            'Your browser blocked the billing portal popup. Click OK to open it in this tab, or Cancel to stay here.'
          );
          if (proceed) {
            window.location.assign(portalUrl);
          } else {
            setSubscriptionBanner({
              type: 'error',
              message: 'Popup blocked. Please allow pop-ups for OppFoundry or use a different browser window.',
            });
          }
        }
      } else {
        throw new Error('No billing portal url returned');
      }
    } catch (error) {
      console.error('Billing portal error:', error);
      setSubscriptionBanner({
        type: 'error',
        message: 'Unable to open the billing portal right now. Please try again or contact support.',
      });
    } finally {
      setIsOpeningBilling(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!pwNew || pwNew !== pwConfirm) {
      setPwStatus({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    setPwSaving(true);
    setPwStatus(null);
    try {
      await authAPI.changePassword(pwCurrent, pwNew, pwConfirm);
      setPwStatus({ type: 'success', message: 'Password updated successfully.' });
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Unable to change password.';
      setPwStatus({ type: 'error', message: msg });
    } finally {
      setPwSaving(false);
    }
  };

  const handleSendSelfResetLink = async () => {
    if (!user?.email) {
      navigate('/forgot-password');
      return;
    }
    setPwResetSending(true);
    setPwStatus(null);
    try {
      await authAPI.requestPasswordReset(user.email);
      setPwStatus({ type: 'success', message: `We sent a reset link to ${user.email}.` });
    } catch (error) {
      setPwStatus({ type: 'error', message: 'Unable to send reset link. Please try again.' });
    } finally {
      setPwResetSending(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:auto-rows-auto items-stretch">
        {/* Profile Information */}
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <UserIcon className="h-7 w-7 text-slate-500" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">Profile Information</h2>
                <p className="mt-1 text-sm text-slate-500">{user.full_name}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isPremium ? (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                  Pro Member
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  Free Account
                </span>
              )}
              {user.google_linked ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                  <svg role="img" aria-hidden className="h-3.5 w-3.5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M23.49 12.27c0-.82-.07-1.64-.22-2.44H12v4.62h6.48a5.54 5.54 0 0 1-2.39 3.63v3h3.86c2.26-2.08 3.54-5.15 3.54-8.81z"/>
                    <path fill="#34A853" d="M12 24c3.22 0 5.93-1.07 7.9-2.92l-3.86-3a7.43 7.43 0 0 1-4.04 1.16c-3.1 0-5.73-2.09-6.67-4.9H1.37v3.09A12 12 0 0 0 12 24z"/>
                    <path fill="#FBBC05" d="M5.33 14.34a7.2 7.2 0 0 1 0-4.68V6.57H1.37a12 12 0 0 0 0 10.86l3.96-3.09z"/>
                    <path fill="#EA4335" d="M12 4.75c1.75 0 3.32.6 4.55 1.78l3.41-3.41C17.93 1.13 15.22 0 12 0A12 12 0 0 0 1.37 6.57l3.96 3.09C5.71 7.84 8.34 4.75 12 4.75z"/>
                  </svg>
                  Connected to Google
                </span>
              ) : (
                <a
                  href={GOOGLE_ACCOUNT_LINK}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <svg role="img" aria-hidden className="h-3.5 w-3.5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M23.49 12.27c0-.82-.07-1.64-.22-2.44H12v4.62h6.48a5.54 5.54 0 0 1-2.39 3.63v3h3.86c2.26-2.08 3.54-5.15 3.54-8.81z"/>
                    <path fill="#34A853" d="M12 24c3.22 0 5.93-1.07 7.9-2.92l-3.86-3a7.43 7.43 0 0 1-4.04 1.16c-3.1 0-5.73-2.09-6.67-4.9H1.37v3.09A12 12 0 0 0 12 24z"/>
                    <path fill="#FBBC05" d="M5.33 14.34a7.2 7.2 0 0 1 0-4.68V6.57H1.37a12 12 0 0 0 0 10.86l3.96-3.09z"/>
                    <path fill="#EA4335" d="M12 4.75c1.75 0 3.32.6 4.55 1.78l3.41-3.41C17.93 1.13 15.22 0 12 0A12 12 0 0 0 1.37 6.57l3.96 3.09C5.71 7.84 8.34 4.75 12 4.75z"/>
                  </svg>
                  Connect Google
                </a>
              )}
              {!isEditingProfile && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingProfile(true);
                    setProfileStatus(null);
                  }}
                  className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>

          {profileStatus && (
            <div
              className={`mt-4 rounded-lg border px-3 py-2 text-sm font-medium ${
                profileStatus.type === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-rose-200 bg-rose-50 text-rose-700'
              }`}
            >
              {profileStatus.message}
            </div>
          )}

          <dl className="mt-6 grid grid-cols-1 gap-5 text-sm text-slate-600 md:grid-cols-2">
            <div>
              <dt className="font-medium text-slate-500">Full Name</dt>
              <dd className="mt-1 text-slate-900">
                {isEditingProfile ? (
                  <input
                    type="text"
                    value={profileForm.full_name}
                    onChange={(event) => handleProfileInputChange('full_name', event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  user.full_name
                )}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">Email Address</dt>
              <dd className="mt-1 text-slate-900">
                {isEditingProfile ? (
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(event) => handleProfileInputChange('email', event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  user.email
                )}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">Business Name</dt>
              <dd className="mt-1 text-slate-900">
                {isEditingProfile ? (
                  <input
                    type="text"
                    value={profileForm.business_name}
                    onChange={(event) => handleProfileInputChange('business_name', event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  user.business_name || 'Not provided'
                )}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">Employee Count</dt>
              <dd className="mt-1 text-slate-900">
                {isEditingProfile ? (
                  <select
                    value={profileForm.employee_count}
                    onChange={(event) => handleProfileInputChange('employee_count', event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select employee count</option>
                    {EMPLOYEE_COUNT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  user.employee_count
                )}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">Business Type</dt>
              <dd className="mt-1 text-slate-900">
                {isEditingProfile ? (
                  <select
                    value={profileForm.business_type}
                    onChange={(event) => handleProfileInputChange('business_type', event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select a business type</option>
                    {BUSINESS_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  user.business_type || 'Not provided'
                )}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">Phone</dt>
              <dd className="mt-1 text-slate-900">
                {isEditingProfile ? (
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(event) => handleProfileInputChange('phone', event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  user.phone || 'Not provided'
                )}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">City</dt>
              <dd className="mt-1 text-slate-900">
                {isEditingProfile ? (
                  <input
                    type="text"
                    value={profileForm.city}
                    onChange={(event) => handleProfileInputChange('city', event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  user.city || 'Not provided'
                )}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">Member Since</dt>
              <dd className="mt-1 text-slate-900">{formatDate(user.created_at)}</dd>
            </div>
          </dl>

          {isEditingProfile && (
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProfileSubmit}
                disabled={!profileChanged || isSavingProfile}
                className="inline-flex items-center rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </section>

        {/* Subscription Overview (right column, span both rows on desktop) */}
        <section className="flex h-full flex-col rounded-2xl border border-blue-200 bg-blue-600/95 p-6 text-blue-50 shadow-sm lg:row-span-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-200">Subscription</p>
              <h2 className="mt-1 text-xl font-semibold text-white">{planLabel} Plan</h2>
            </div>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${
                hasActiveSubscription
                  ? 'border-emerald-200/80 bg-emerald-100/20 text-emerald-100'
                  : 'border-blue-300 bg-blue-500 text-blue-100'
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${hasActiveSubscription ? 'bg-emerald-300' : 'bg-blue-200'}`} />
              {hasActiveSubscription ? statusLabel : 'Pro Inactive'}
            </span>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <p className="text-3xl font-semibold text-white">{priceDisplay}</p>
              <p className="text-sm text-blue-100">
                {isPremium ? billingSubtitle : 'Upgrade to unlock pro features'}
              </p>
            </div>

            <div className="rounded-xl bg-white/10 p-4 text-sm text-blue-50">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-200">What’s included</p>
              <ul className="mt-3 space-y-2">
                {proBenefits.map((benefit) => (
                  <li key={benefit.label} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white/20">
                      {benefit.icon}
                    </span>
                    <span className="leading-relaxed">{benefit.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {isPremium ? (
              <>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/preferences"
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-blue-200/40 bg-white/10 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/20 sm:flex-none sm:min-w-[190px]"
                  >
                    <Cog6ToothIcon className="h-4 w-4" />
                    <span className="whitespace-nowrap text-center">Match Preferences</span>
                  </Link>
                  {canManageBilling ? (
                    <button
                      type="button"
                      onClick={handleManageBilling}
                      disabled={isOpeningBilling}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/30 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:min-w-[190px]"
                    >
                      <CreditCardIcon className="h-4 w-4" />
                      <span className="whitespace-nowrap text-center">
                        {isOpeningBilling ? 'Opening Portal...' : isCanceledAtPeriodEnd ? 'Renew Subscription' : 'Manage Billing'}
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleUpgrade}
                      disabled={isUpgrading}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:opacity-60 sm:flex-none sm:min-w-[190px]"
                    >
                      <CreditCardIcon className="h-4 w-4" />
                      <span className="text-center">{isUpgrading ? 'Opening Checkout...' : 'Subscribe to keep Pro'}</span>
                    </button>
                  )}
                  {canManageBilling && !isCanceledAtPeriodEnd && (
                    <button
                      type="button"
                      onClick={handleCancelSubscription}
                      disabled={!hasActiveSubscription || isCanceling}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/30 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:min-w-[190px]"
                    >
                      <XMarkIcon className="h-4 w-4" />
                      <span className="whitespace-nowrap text-center">{isCanceling ? 'Cancelling...' : 'Cancel Subscription'}</span>
                    </button>
                  )}
                </div>
                {!canManageBilling && isTrialActive && (
                  <p className="mt-2 w-full text-xs text-blue-100">
                    No payment method is on file. Your free Pro access ends {trialEndsLabel}; subscribing before then keeps the remaining days free.
                  </p>
                )}
              </>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={isUpgrading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm transition hover:bg-blue-50"
              >
                <CreditCardIcon className="h-4 w-4" />
                {isUpgrading ? 'Processing...' : 'Upgrade to Pro'}
              </button>
            )}
          </div>

          <dl className="mt-6 grid gap-4 text-sm text-blue-100">
            <div>
              <dt className="font-medium text-blue-200">{billingLabel}</dt>
              <dd className="mt-1 text-white">{(isPremium || isTrialActive) ? billingValue : '—'}</dd>
            </div>
          </dl>

          {subscriptionBanner && (
            <div
              className={`mt-4 rounded-lg border px-3 py-2 text-xs font-medium ${
                subscriptionBanner.type === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-rose-200 bg-rose-50 text-rose-700'
              }`}
            >
              {subscriptionBanner.message}
            </div>
          )}

          {cancelsOn && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
              Pro access remains active through {cancelsOn}.
            </div>
          )}
        </section>

        <NotificationSettings />
        {/* Password management (left column width) */}
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-slate-500"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0V13.5m-.75 8.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 12.75v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/></svg>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900">Password</h2>
          </div>

          {pwStatus && (
            <div className={`mt-4 rounded-lg border px-3 py-2 text-sm font-medium ${pwStatus.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
              {pwStatus.message}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-4">
            {user.has_password && (
              <div>
                <label className="block text-sm font-medium text-slate-600">Current password</label>
                <input type="password" className="mt-1 w-full lg:max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" value={pwCurrent} onChange={(e)=>setPwCurrent(e.target.value)} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-600">New password</label>
              <input type="password" className="mt-1 w-full lg:max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" value={pwNew} onChange={(e)=>setPwNew(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600">Confirm new password</label>
              <input type="password" className="mt-1 w-full lg:max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" value={pwConfirm} onChange={(e)=>setPwConfirm(e.target.value)} />
            </div>
          </div>
          <div className="mt-4">
            <button onClick={handlePasswordChange} disabled={pwSaving} className="inline-flex items-center rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 disabled:opacity-60">
              {pwSaving ? 'Saving…' : (user.has_password ? 'Change password' : 'Set password')}
            </button>
            {user.has_password && (
              <p className="mt-2 text-xs text-slate-500">
                Don't know your current password?{' '}
                <button type="button" onClick={handleSendSelfResetLink} disabled={pwResetSending} className="text-blue-600 hover:underline disabled:opacity-60">
                  {pwResetSending ? 'Sending…' : 'Email yourself a reset link'}
                </button>
                .
              </p>
            )}
          </div>
        </section>

        {/* Danger zone: Account deletion */}
        <section className="rounded-2xl border border-rose-200 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-rose-600" viewBox="0 0 24 24" fill="none"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14.74 9l-.346 9m-4.788 0L9.26 9M4 6h16m-1 0l-.867 12.142A2 2 0 0 1 16.138 20H7.862a2 2 0 0 1-1.995-1.858L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </div>
            <h2 className="text-2xl font-semibold text-rose-700">Delete account</h2>
          </div>

          {delStatus && (
            <div className={`mt-4 rounded-lg border px-3 py-2 text-sm font-medium ${delStatus.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
              {delStatus.message}
            </div>
          )}

          <p className="mt-4 text-sm text-slate-600">This will deactivate your account. If you have an active subscription, it will be canceled at the end of the current billing period.</p>

          <div className="mt-4 grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600">Type DELETE to confirm</label>
              <input type="text" className="mt-1 w-full lg:max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500" value={delConfirm} onChange={(e)=>setDelConfirm(e.target.value)} />
            </div>
            {user.has_password && (
              <div>
                <label className="block text-sm font-medium text-slate-600">Current password</label>
                <input type="password" className="mt-1 w-full lg:max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500" value={delPassword} onChange={(e)=>setDelPassword(e.target.value)} />
              </div>
            )}
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={async ()=>{
                setDelBusy(true);
                setDelStatus(null);
                try {
                  const payload: any = { confirm: delConfirm };
                  if (user.has_password) payload.password = delPassword;
                  const resp = await authAPI.deleteAccount(payload);
                  if (resp.data?.success) {
                    setDelStatus({ type: 'success', message: 'Your account has been deactivated.' });
                    window.location.href = '/';
                  } else {
                    setDelStatus({ type: 'error', message: (resp.data as any)?.error || 'Unable to delete account.' });
                  }
                } catch (err: any) {
                  setDelStatus({ type: 'error', message: err?.response?.data?.error || 'Unable to delete account.' });
                } finally {
                  setDelBusy(false);
                }
              }}
              disabled={delBusy || delConfirm.toUpperCase() !== 'DELETE' || (user.has_password && !delPassword)}
              className="inline-flex items-center rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-500 disabled:opacity-60"
            >
              {delBusy ? 'Deleting…' : 'Delete account'}
            </button>
          </div>
        </section>

        {/* (duplicate subscription card removed) */}
      </div>
      
    </div>
  );
};

export default Account;
